import React, { useState, useEffect, useCallback } from 'react';
import {
  collection, query, orderBy, onSnapshot,
  addDoc, updateDoc, deleteDoc, doc, Timestamp, where,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { useTecnicos } from '../hooks/useTecnicos';

// ── Helpers ────────────────────────────────────────────────────────────────────
const RECORD_TYPES = [
  { key: 'mantenimiento', label: 'Mantenimiento',  bg: 'bg-blue-100',   text: 'text-blue-700' },
  { key: 'reparacion',    label: 'Reparación',     bg: 'bg-red-100',    text: 'text-red-700' },
  { key: 'combustible',   label: 'Combustible',    bg: 'bg-yellow-100', text: 'text-yellow-700' },
  { key: 'itv',           label: 'ITV',            bg: 'bg-purple-100', text: 'text-purple-700' },
  { key: 'neumaticos',    label: 'Neumáticos',     bg: 'bg-gray-100',   text: 'text-gray-600' },
  { key: 'seguro',        label: 'Seguro',         bg: 'bg-green-100',  text: 'text-green-700' },
  { key: 'otro',          label: 'Otro',           bg: 'bg-orange-100', text: 'text-orange-700' },
];

const rtMap = Object.fromEntries(RECORD_TYPES.map(r => [r.key, r]));

function fmt(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr + 'T12:00:00').toLocaleDateString('es-ES', {
    day: '2-digit', month: '2-digit', year: 'numeric',
  });
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr + 'T12:00:00') - new Date()) / 86400000);
}

function ExpiryBadge({ dateStr, label }) {
  const days = daysUntil(dateStr);
  if (!dateStr) return <span className="text-xs text-gray-400">Sin fecha</span>;
  const cls =
    days < 0     ? 'bg-red-100 text-red-700'   :
    days <= 30   ? 'bg-amber-100 text-amber-700' :
                   'bg-green-100 text-green-700';
  const txt =
    days < 0     ? `Vencido hace ${Math.abs(days)}d` :
    days === 0   ? 'Vence hoy'                        :
    days <= 30   ? `Vence en ${days}d`                :
                   fmt(dateStr);
  return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${cls}`}>{txt}</span>;
}

function TypeBadge({ type }) {
  const cfg = rtMap[type] || { label: type, bg: 'bg-gray-100', text: 'text-gray-600' };
  return (
    <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

const RECORD_INIT = {
  type: 'mantenimiento',
  date: new Date().toISOString().split('T')[0],
  cost: '', mileage: '', description: '', provider: '', notes: '',
};

const INCIDENT_INIT = {
  title: '',
  date: new Date().toISOString().split('T')[0],
  description: '',
  notes: '',
};

const VEHICLE_FIELDS = [
  { key: 'plate',       label: 'Matrícula',     type: 'text',   required: true },
  { key: 'brand',       label: 'Marca',         type: 'text',   required: true },
  { key: 'model',       label: 'Modelo',        type: 'text',   required: true },
  { key: 'year',        label: 'Año',           type: 'number' },
  { key: 'color',       label: 'Color',         type: 'text' },
  { key: 'vin',         label: 'Bastidor (VIN)',type: 'text' },
  { key: 'currentMileage', label: 'Km actuales', type: 'number' },
];

// ── Componente principal ──────────────────────────────────────────────────────
export default function FleetVehicleDetail({ vehicle, onBack, onVehicleUpdated }) {
  const [records, setRecords]           = useState([]);
  const [loadingRec, setLoadingRec]     = useState(true);
  const [typeFilter, setTypeFilter]     = useState('all');
  const [showAddRecord, setShowAddRecord] = useState(false);
  const [recordForm, setRecordForm]     = useState(RECORD_INIT);
  const [savingRec, setSavingRec]       = useState(false);
  const [deletingId, setDeletingId]     = useState(null);
  const [showEditVehicle, setShowEditVehicle] = useState(false);
  const [editForm, setEditForm]         = useState({});
  const [savingVehicle, setSavingVehicle] = useState(false);
  const { tecnicos } = useTecnicos();

  const [incidents, setIncidents]           = useState([]);
  const [loadingInc, setLoadingInc]         = useState(true);
  const [showAddIncident, setShowAddIncident] = useState(false);
  const [incidentForm, setIncidentForm]     = useState(INCIDENT_INIT);
  const [savingInc, setSavingInc]           = useState(false);
  const [resolvingId, setResolvingId]       = useState(null);
  const [deletingIncId, setDeletingIncId]   = useState(null);

  // Cargar registros en tiempo real
  useEffect(() => {
    const q = query(
      collection(db, 'fleet', vehicle.id, 'records'),
      orderBy('date', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setRecords(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingRec(false);
    }, err => { console.error(err); setLoadingRec(false); });
    return unsub;
  }, [vehicle.id]);

  // Cargar incidencias en tiempo real
  useEffect(() => {
    const q = query(
      collection(db, 'fleet', vehicle.id, 'incidents'),
      orderBy('date', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setIncidents(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoadingInc(false);
    }, err => { console.error(err); setLoadingInc(false); });
    return unsub;
  }, [vehicle.id]);

  // ── Registros ────────────────────────────────────────────────────────────────
  const handleAddRecord = useCallback(async () => {
    if (!recordForm.date || !recordForm.description) return;
    setSavingRec(true);
    try {
      await addDoc(collection(db, 'fleet', vehicle.id, 'records'), {
        ...recordForm,
        cost:    recordForm.cost    ? parseFloat(recordForm.cost)    : 0,
        mileage: recordForm.mileage ? parseInt(recordForm.mileage)   : null,
        createdAt: Timestamp.now(),
      });
      setRecordForm(RECORD_INIT);
      setShowAddRecord(false);
    } catch (e) { console.error(e); }
    setSavingRec(false);
  }, [recordForm, vehicle.id]);

  const handleDeleteRecord = useCallback(async (recId) => {
    if (!window.confirm('¿Eliminar este registro?')) return;
    setDeletingId(recId);
    await deleteDoc(doc(db, 'fleet', vehicle.id, 'records', recId));
    setDeletingId(null);
  }, [vehicle.id]);

  // ── Incidencias ──────────────────────────────────────────────────────────────
  const handleAddIncident = useCallback(async () => {
    if (!incidentForm.title || !incidentForm.date) return;
    setSavingInc(true);
    try {
      await addDoc(collection(db, 'fleet', vehicle.id, 'incidents'), {
        ...incidentForm,
        status: 'abierta',
        resolvedDate: null,
        createdAt: Timestamp.now(),
      });
      setIncidentForm(INCIDENT_INIT);
      setShowAddIncident(false);
    } catch (e) { console.error(e); }
    setSavingInc(false);
  }, [incidentForm, vehicle.id]);

  const handleResolveIncident = useCallback(async (incId) => {
    setResolvingId(incId);
    try {
      await updateDoc(doc(db, 'fleet', vehicle.id, 'incidents', incId), {
        status: 'resuelta',
        resolvedDate: new Date().toISOString().split('T')[0],
      });
    } catch (e) { console.error(e); }
    setResolvingId(null);
  }, [vehicle.id]);

  const handleDeleteIncident = useCallback(async (incId) => {
    if (!window.confirm('¿Eliminar esta incidencia?')) return;
    setDeletingIncId(incId);
    await deleteDoc(doc(db, 'fleet', vehicle.id, 'incidents', incId));
    setDeletingIncId(null);
  }, [vehicle.id]);

  // ── Editar vehículo ─────────────────────────────────────────────────────────
  const openEdit = () => {
    setEditForm({
      plate: vehicle.plate || '',
      brand: vehicle.brand || '',
      model: vehicle.model || '',
      year:  vehicle.year  || '',
      color: vehicle.color || '',
      vin:   vehicle.vin   || '',
      currentMileage: vehicle.currentMileage || '',
      assignedEmployeeId: vehicle.assignedEmployeeId || '',
      status: vehicle.status || 'activo',
      notes: vehicle.notes || '',
      insuranceCompany:      vehicle.insuranceCompany      || '',
      insurancePolicyNumber: vehicle.insurancePolicyNumber || '',
      insuranceExpiryDate:   vehicle.insuranceExpiryDate   || '',
      insuranceAnnualCost:   vehicle.insuranceAnnualCost   || '',
      itvLastDate:  vehicle.itvLastDate  || '',
      itvNextDate:  vehicle.itvNextDate  || '',
      itvResult:    vehicle.itvResult    || '',
    });
    setShowEditVehicle(true);
  };

  const handleSaveVehicle = useCallback(async () => {
    if (!editForm.plate || !editForm.brand || !editForm.model) return;
    setSavingVehicle(true);
    try {
      const payload = {
        ...editForm,
        year:          editForm.year          ? parseInt(editForm.year)          : null,
        currentMileage: editForm.currentMileage ? parseInt(editForm.currentMileage) : null,
        insuranceAnnualCost: editForm.insuranceAnnualCost ? parseFloat(editForm.insuranceAnnualCost) : null,
        updatedAt: Timestamp.now(),
      };
      await updateDoc(doc(db, 'fleet', vehicle.id), payload);
      setShowEditVehicle(false);
      onVehicleUpdated?.({ id: vehicle.id, ...payload });
    } catch (e) { console.error(e); }
    setSavingVehicle(false);
  }, [editForm, vehicle.id, onVehicleUpdated]);

  // ── Derivados ────────────────────────────────────────────────────────────────
  const filtered = typeFilter === 'all' ? records : records.filter(r => r.type === typeFilter);
  const openIncidents = incidents.filter(i => i.status === 'abierta').length;

  const totalByType = records.reduce((acc, r) => {
    acc[r.type] = (acc[r.type] || 0) + (r.cost || 0);
    return acc;
  }, {});

  const grandTotal = Object.values(totalByType).reduce((s, v) => s + v, 0);

  const techName = tecnicos.find(t => String(t.employeeId) === String(vehicle.assignedEmployeeId))?.nombre || '—';

  const STATUS_CFG = {
    activo: 'bg-green-100 text-green-700',
    taller: 'bg-amber-100 text-amber-700',
    baja:   'bg-gray-100 text-gray-500',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b px-4 py-3 flex items-center gap-3">
        <button onClick={onBack} className="p-2 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0">
          <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-bold text-gray-900">{vehicle.plate}</h1>
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_CFG[vehicle.status] || STATUS_CFG.activo}`}>
              {vehicle.status || 'activo'}
            </span>
          </div>
          <p className="text-sm text-gray-500 truncate">{vehicle.brand} {vehicle.model} {vehicle.year ? `· ${vehicle.year}` : ''}</p>
        </div>
        <button onClick={openEdit}
          className="flex-shrink-0 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors">
          Editar
        </button>
      </div>

      <div className="p-4 space-y-4 max-w-2xl mx-auto">

        {/* Info general */}
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-white rounded-xl border p-3 col-span-2 sm:col-span-1">
            <p className="text-xs text-gray-500 mb-1">Datos del vehículo</p>
            <div className="space-y-1 text-sm">
              {vehicle.color       && <p><span className="text-gray-400">Color:</span> {vehicle.color}</p>}
              {vehicle.vin         && <p><span className="text-gray-400">Bastidor:</span> <span className="font-mono text-xs">{vehicle.vin}</span></p>}
              {vehicle.currentMileage && <p><span className="text-gray-400">Km:</span> <strong>{Number(vehicle.currentMileage).toLocaleString('es-ES')}</strong></p>}
              <p><span className="text-gray-400">Técnico:</span> {techName}</p>
            </div>
          </div>

          {/* ITV */}
          <div className="bg-white rounded-xl border p-3">
            <p className="text-xs text-gray-500 mb-2">ITV</p>
            <ExpiryBadge dateStr={vehicle.itvNextDate} label="Próxima ITV" />
            <div className="mt-2 space-y-0.5 text-xs text-gray-500">
              {vehicle.itvLastDate && <p>Última: {fmt(vehicle.itvLastDate)}</p>}
              {vehicle.itvResult   && <p>Resultado: {vehicle.itvResult}</p>}
            </div>
          </div>

          {/* Seguro */}
          <div className="bg-white rounded-xl border p-3">
            <p className="text-xs text-gray-500 mb-2">Seguro</p>
            <ExpiryBadge dateStr={vehicle.insuranceExpiryDate} label="Vencimiento seguro" />
            <div className="mt-2 space-y-0.5 text-xs text-gray-500">
              {vehicle.insuranceCompany && <p>{vehicle.insuranceCompany}</p>}
              {vehicle.insurancePolicyNumber && <p>Póliza: {vehicle.insurancePolicyNumber}</p>}
              {vehicle.insuranceAnnualCost  && <p>{Number(vehicle.insuranceAnnualCost).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}/año</p>}
            </div>
          </div>
        </div>

        {/* Resumen de costes */}
        {records.length > 0 && (
          <div className="bg-white rounded-xl border p-4">
            <p className="text-xs text-gray-500 mb-3">Resumen de costes</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {RECORD_TYPES.filter(rt => totalByType[rt.key]).map(rt => (
                <div key={rt.key} className={`flex items-center gap-1.5 text-xs rounded-lg px-2.5 py-1.5 ${rt.bg} ${rt.text}`}>
                  <span className="font-medium">{rt.label}</span>
                  <span className="font-bold">{totalByType[rt.key].toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}</span>
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between border-t pt-2">
              <span className="text-sm text-gray-600 font-medium">Total registrado</span>
              <span className="text-base font-bold text-gray-900">
                {grandTotal.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
              </span>
            </div>
          </div>
        )}

        {/* Incidencias */}
        <div className="bg-white rounded-xl border">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="flex items-center gap-2">
              <h2 className="font-semibold text-gray-900">Incidencias de taller</h2>
              {openIncidents > 0 && (
                <span className="text-xs font-bold bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                  {openIncidents} abierta{openIncidents > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <button
              onClick={() => { setIncidentForm(INCIDENT_INIT); setShowAddIncident(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Nueva
            </button>
          </div>

          {loadingInc ? (
            <div className="flex items-center justify-center py-8 text-gray-400 text-sm gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Cargando…
            </div>
          ) : incidents.length === 0 ? (
            <div className="py-10 text-center text-gray-400">
              <p className="text-sm">Sin incidencias registradas</p>
              <p className="text-xs mt-1 text-gray-300">Añade una cuando el vehículo entre en taller</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {incidents.map(inc => (
                <div key={inc.id} className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${
                          inc.status === 'abierta'
                            ? 'bg-red-100 text-red-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {inc.status === 'abierta' ? 'Abierta' : 'Resuelta'}
                        </span>
                        <span className="text-xs text-gray-400">{fmt(inc.date)}</span>
                        {inc.resolvedDate && (
                          <span className="text-xs text-gray-400">→ resuelta {fmt(inc.resolvedDate)}</span>
                        )}
                      </div>
                      <p className="text-sm font-medium text-gray-800">{inc.title}</p>
                      {inc.description && <p className="text-xs text-gray-500 mt-0.5">{inc.description}</p>}
                      {inc.notes && <p className="text-xs text-gray-400 mt-0.5 italic">{inc.notes}</p>}
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {inc.status === 'abierta' && (
                        <button
                          onClick={() => handleResolveIncident(inc.id)}
                          disabled={resolvingId === inc.id}
                          className="text-xs px-2 py-1 bg-green-50 hover:bg-green-100 text-green-700 rounded-lg transition-colors disabled:opacity-50"
                        >
                          {resolvingId === inc.id ? '…' : 'Resolver'}
                        </button>
                      )}
                      <button
                        onClick={() => handleDeleteIncident(inc.id)}
                        disabled={deletingIncId === inc.id}
                        className="p-1 text-gray-300 hover:text-red-500 transition-colors rounded"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Historial */}
        <div className="bg-white rounded-xl border">
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <h2 className="font-semibold text-gray-900">Historial</h2>
            <button
              onClick={() => { setRecordForm(RECORD_INIT); setShowAddRecord(true); }}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-900 hover:bg-gray-700 text-white text-xs font-medium rounded-lg transition-colors"
            >
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Añadir
            </button>
          </div>

          {/* Filtros de tipo */}
          <div className="flex gap-2 px-4 py-2 overflow-x-auto border-b">
            {[{ key: 'all', label: 'Todos' }, ...RECORD_TYPES].map(rt => (
              <button key={rt.key}
                onClick={() => setTypeFilter(rt.key)}
                className={`flex-shrink-0 text-xs px-3 py-1.5 rounded-lg transition-colors ${
                  typeFilter === rt.key
                    ? 'bg-gray-900 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {rt.label}
              </button>
            ))}
          </div>

          {loadingRec ? (
            <div className="flex items-center justify-center py-10 text-gray-400 text-sm gap-2">
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
              </svg>
              Cargando…
            </div>
          ) : filtered.length === 0 ? (
            <div className="py-12 text-center text-gray-400">
              <p className="text-sm">Sin registros{typeFilter !== 'all' ? ' de este tipo' : ''}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtered.map(rec => (
                <div key={rec.id} className="flex items-start gap-3 px-4 py-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <TypeBadge type={rec.type} />
                      <span className="text-xs text-gray-400">{fmt(rec.date)}</span>
                      {rec.mileage && <span className="text-xs text-gray-400">{Number(rec.mileage).toLocaleString('es-ES')} km</span>}
                    </div>
                    <p className="text-sm text-gray-800">{rec.description}</p>
                    {rec.provider && <p className="text-xs text-gray-400 mt-0.5">{rec.provider}</p>}
                    {rec.notes    && <p className="text-xs text-gray-500 mt-0.5 italic">{rec.notes}</p>}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    {rec.cost > 0 && (
                      <span className="text-sm font-bold text-gray-900">
                        {rec.cost.toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })}
                      </span>
                    )}
                    <button
                      onClick={() => handleDeleteRecord(rec.id)}
                      disabled={deletingId === rec.id}
                      className="p-1 text-gray-300 hover:text-red-500 transition-colors rounded"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Modal añadir incidencia ──────────────────────────────────────────── */}
      {showAddIncident && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowAddIncident(false)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Nueva incidencia</h3>
              <button onClick={() => setShowAddIncident(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Título *</label>
                <input type="text" placeholder="Avería frenos, Ruido motor…" value={incidentForm.title}
                  onChange={e => setIncidentForm(f => ({ ...f, title: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 focus:border-transparent" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Fecha de entrada en taller *</label>
                <input type="date" value={incidentForm.date}
                  onChange={e => setIncidentForm(f => ({ ...f, date: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Descripción del problema</label>
                <textarea rows={3} placeholder="Describe la avería o motivo de entrada al taller…" value={incidentForm.description}
                  onChange={e => setIncidentForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 resize-none" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notas adicionales</label>
                <textarea rows={2} placeholder="Presupuesto esperado, taller asignado, contacto…" value={incidentForm.notes}
                  onChange={e => setIncidentForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-amber-500 resize-none" />
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-2">
              <button onClick={() => setShowAddIncident(false)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={handleAddIncident} disabled={savingInc || !incidentForm.title || !incidentForm.date}
                className="flex-1 py-2.5 bg-amber-600 hover:bg-amber-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors">
                {savingInc ? 'Guardando…' : 'Registrar incidencia'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal añadir registro ─────────────────────────────────────────────── */}
      {showAddRecord && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowAddRecord(false)}>
          <div className="bg-white w-full sm:max-w-md rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Añadir registro</h3>
              <button onClick={() => setShowAddRecord(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tipo *</label>
                <select value={recordForm.type} onChange={e => setRecordForm(f => ({ ...f, type: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 focus:border-transparent">
                  {RECORD_TYPES.map(rt => <option key={rt.key} value={rt.key}>{rt.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fecha *</label>
                  <input type="date" value={recordForm.date} onChange={e => setRecordForm(f => ({ ...f, date: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Coste (€)</label>
                  <input type="number" min="0" step="0.01" placeholder="0.00" value={recordForm.cost}
                    onChange={e => setRecordForm(f => ({ ...f, cost: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Descripción *</label>
                <input type="text" placeholder="Cambio aceite y filtros…" value={recordForm.description}
                  onChange={e => setRecordForm(f => ({ ...f, description: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Km al realizar</label>
                  <input type="number" min="0" placeholder="45000" value={recordForm.mileage}
                    onChange={e => setRecordForm(f => ({ ...f, mileage: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Proveedor / Taller</label>
                  <input type="text" placeholder="Taller Martínez" value={recordForm.provider}
                    onChange={e => setRecordForm(f => ({ ...f, provider: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                <textarea rows={2} placeholder="Observaciones adicionales…" value={recordForm.notes}
                  onChange={e => setRecordForm(f => ({ ...f, notes: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 resize-none" />
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-2">
              <button onClick={() => setShowAddRecord(false)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={handleAddRecord} disabled={savingRec || !recordForm.date || !recordForm.description}
                className="flex-1 py-2.5 bg-gray-900 hover:bg-gray-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors">
                {savingRec ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal editar vehículo ─────────────────────────────────────────────── */}
      {showEditVehicle && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={() => setShowEditVehicle(false)}>
          <div className="bg-white w-full sm:max-w-lg rounded-t-2xl sm:rounded-2xl shadow-2xl max-h-[92vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}>
            <div className="px-5 py-4 border-b flex items-center justify-between">
              <h3 className="font-bold text-gray-900">Editar vehículo</h3>
              <button onClick={() => setShowEditVehicle(false)} className="text-gray-400 hover:text-gray-600">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-4">
              {/* Datos básicos */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">Datos del vehículo</p>
              <div className="grid grid-cols-2 gap-3">
                {VEHICLE_FIELDS.map(f => (
                  <div key={f.key} className={f.key === 'plate' || f.key === 'vin' ? 'col-span-2' : ''}>
                    <label className="block text-xs font-medium text-gray-700 mb-1">{f.label}{f.required ? ' *' : ''}</label>
                    <input type={f.type || 'text'} value={editForm[f.key] || ''}
                      onChange={e => setEditForm(fm => ({ ...fm, [f.key]: e.target.value }))}
                      className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                  </div>
                ))}
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Técnico asignado</label>
                  <select value={editForm.assignedEmployeeId || ''}
                    onChange={e => setEditForm(fm => ({ ...fm, assignedEmployeeId: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900">
                    <option value="">Sin asignar</option>
                    {tecnicos.map(t => <option key={t.id} value={t.employeeId}>{t.nombre}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Estado</label>
                  <select value={editForm.status || 'activo'}
                    onChange={e => setEditForm(fm => ({ ...fm, status: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900">
                    <option value="activo">Activo</option>
                    <option value="taller">En taller</option>
                    <option value="baja">Baja</option>
                  </select>
                </div>
              </div>

              {/* Seguro */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2">Seguro</p>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Aseguradora</label>
                  <input type="text" value={editForm.insuranceCompany || ''}
                    onChange={e => setEditForm(fm => ({ ...fm, insuranceCompany: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Nº póliza</label>
                  <input type="text" value={editForm.insurancePolicyNumber || ''}
                    onChange={e => setEditForm(fm => ({ ...fm, insurancePolicyNumber: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Coste anual (€)</label>
                  <input type="number" min="0" step="0.01" value={editForm.insuranceAnnualCost || ''}
                    onChange={e => setEditForm(fm => ({ ...fm, insuranceAnnualCost: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Vencimiento seguro</label>
                  <input type="date" value={editForm.insuranceExpiryDate || ''}
                    onChange={e => setEditForm(fm => ({ ...fm, insuranceExpiryDate: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
              </div>

              {/* ITV */}
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide pt-2">ITV</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Última ITV</label>
                  <input type="date" value={editForm.itvLastDate || ''}
                    onChange={e => setEditForm(fm => ({ ...fm, itvLastDate: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Próxima ITV</label>
                  <input type="date" value={editForm.itvNextDate || ''}
                    onChange={e => setEditForm(fm => ({ ...fm, itvNextDate: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-medium text-gray-700 mb-1">Resultado ITV</label>
                  <select value={editForm.itvResult || ''}
                    onChange={e => setEditForm(fm => ({ ...fm, itvResult: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900">
                    <option value="">Sin resultado</option>
                    <option value="Favorable">Favorable</option>
                    <option value="Favorable con defectos leves">Favorable con defectos leves</option>
                    <option value="Desfavorable">Desfavorable</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                <textarea rows={2} value={editForm.notes || ''}
                  onChange={e => setEditForm(fm => ({ ...fm, notes: e.target.value }))}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-gray-900 resize-none" />
              </div>
            </div>
            <div className="px-5 pb-5 flex gap-2">
              <button onClick={() => setShowEditVehicle(false)}
                className="flex-1 py-2.5 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
                Cancelar
              </button>
              <button onClick={handleSaveVehicle}
                disabled={savingVehicle || !editForm.plate || !editForm.brand || !editForm.model}
                className="flex-1 py-2.5 bg-gray-900 hover:bg-gray-700 disabled:opacity-50 text-white text-sm font-medium rounded-xl transition-colors">
                {savingVehicle ? 'Guardando…' : 'Guardar cambios'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
